import {useCallback, useMemo} from 'react';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    horizontalListSortingStrategy,
    rectSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {isAuthError} from '../api';
import {useToast} from './Toast';
import Spinner from './Spinner';
import Checklist from './Checklist';
import NewChecklistModal from './NewChecklistModal';
import GroupSidebar from './GroupSidebar';
import {GridIcon, ColumnsIcon} from './icons';
import TemplateContainer from './TemplateContainer';
import AllItemsView from './AllItemsView';
import ScheduledView from './ScheduledView';
import useChecklists from '../hooks/useChecklists';
import useGroups from '../hooks/useGroups';
import useChecklistViews, {SPECIAL_VIEWS} from '../hooks/useChecklistViews';

function SortableChecklist({checklist, isDragEnabled, ...props}) {
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id: checklist.id, disabled: !isDragEnabled});
    const {role, 'aria-disabled': _ariaDisabled, ...safeAttrs} = attributes;
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : undefined,
        opacity: isDragging ? 0.5 : 1,
    };
    return (
        <div ref={setNodeRef} style={style} className="sortable-checklist-wrapper" id={`checklist-${checklist.id}`} {...safeAttrs}>
            <Checklist data={checklist} dragListeners={isDragEnabled ? listeners : null} isDragEnabled={isDragEnabled} {...props} />
        </div>
    );
}

export default function ChecklistContainer({sidebarOpen, onViewLabelChange}) {
    const showToast = useToast();
    const onError = useCallback((err) => { if (!isAuthError(err)) showToast(err.message); }, [showToast]);

    const cl = useChecklists({onError});
    const {groups, createGroup, renameGroup, deleteGroup: rawDeleteGroup, reorderGroups} = useGroups({initialGroups: cl.initialGroups, onError});
    const views = useChecklistViews({checklistData: cl.checklistData, groups, onViewLabelChange});

    const deleteGroup = useCallback(async (id) => {
        await rawDeleteGroup(id);
        views.handleDeletedGroup(id);
    }, [rawDeleteGroup, views]);

    const deleteChecklist = useCallback(async (id) => {
        await cl.deleteChecklist(id);
        views.handleDeletedChecklist(id);
    }, [cl, views]);

    const effectiveGroupId = useCallback((groupId) => {
        return groupId || (views.selectedGroupId && !SPECIAL_VIEWS.includes(views.selectedGroupId) ? views.selectedGroupId : null);
    }, [views]);

    const addChecklist = useCallback(async (title, groupId) => {
        const created = await cl.addChecklist(title, effectiveGroupId(groupId));
        if (created) views.handleChecklistCreated(created);
    }, [cl, effectiveGroupId, views]);

    const handleModalConfirm = useCallback(async (title, templateId, groupId) => {
        cl.closeModal();
        const gid = effectiveGroupId(groupId);
        if (templateId) {
            const created = await cl.spawnFromTemplate(title, templateId, gid);
            if (created) views.handleChecklistCreated(created);
        } else {
            await addChecklist(title, groupId);
        }
    }, [cl, effectiveGroupId, addChecklist, views]);

    const handleSpawn = useCallback((checklist) => {
        cl.appendChecklist(checklist);
        views.handleSpawn(checklist);
        setTimeout(() => {
            const el = document.getElementById(`checklist-${checklist.id}`);
            if (el) el.scrollIntoView({behavior: 'smooth', block: 'center'});
        }, 100);
    }, [cl, views]);

    const checklistSensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 8}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates}),
    );

    const handleChecklistDragEnd = useCallback((event) => {
        const {active, over} = event;
        if (!over || active.id === over.id) return;
        const oldIndex = views.visibleChecklists.findIndex(c => c.id === active.id);
        const newIndex = views.visibleChecklists.findIndex(c => c.id === over.id);
        const reordered = arrayMove(views.visibleChecklists, oldIndex, newIndex);
        const afterId = newIndex > 0 ? reordered[newIndex - 1].id : null;
        cl.reorderChecklists(active.id, afterId, reordered);
    }, [views.visibleChecklists, cl]);

    const itemCallbacks = useMemo(() => ({
        onToggleItem: cl.toggleItem,
        onDeleteItem: cl.deleteItem,
        onUpdateItem: cl.updateItem,
        onStarItem: cl.starItem,
        onSetDueDateItem: cl.setDueDate,
    }), [cl.toggleItem, cl.deleteItem, cl.updateItem, cl.starItem, cl.setDueDate]);

    const checklistCallbacks = useMemo(() => ({
        ...itemCallbacks,
        onDelete: deleteChecklist,
        onAddItem: cl.addItem,
        onUpdateTitle: cl.updateTitle,
        onReorderItems: cl.reorderItems,
        onToggleSortMode: cl.toggleSortMode,
        onToggleSinkChecked: cl.toggleSinkChecked,
        onToggleHideChecked: cl.toggleHideChecked,
        onFocus: views.focusChecklist,
        onUnfocus: views.unfocusChecklist,
    }), [itemCallbacks, deleteChecklist, cl.addItem, cl.updateTitle, cl.reorderItems, cl.toggleSortMode, cl.toggleSinkChecked, cl.toggleHideChecked, views.focusChecklist, views.unfocusChecklist]);

    return (
        <div className="checklist-layout">
            <GroupSidebar
                groups={groups}
                selectedGroupId={views.selectedGroupId}
                checklistData={cl.checklistData}
                onSelect={views.selectGroup}
                onAddGroup={createGroup}
                onRenameGroup={renameGroup}
                onDeleteGroup={deleteGroup}
                onReorderGroups={reorderGroups}
                onTemplates={views.showTemplates}
                isTemplatesActive={views.innerView === 'templates'}
                hasDueDateItems={cl.checklistData.some(c => c.items.some(i => i.dueDate))}
                sidebarOpen={sidebarOpen}
            />
            {cl.loading ? (
                <Spinner size={28} className="spinner-center" />
            ) : views.innerView === 'templates' ? (
                <TemplateContainer groups={groups} onSpawn={handleSpawn} />
            ) : (
                <div className="checklist-container-root">
                    {views.focusedChecklist ? (
                        <>
                            <div className="focus-breadcrumb-bar">
                                <button className="focus-breadcrumb-link" onClick={views.unfocusChecklist}>
                                    {(() => {
                                        const group = groups.find(g => g.id === views.focusedChecklist.groupId);
                                        return group ? group.name : (SPECIAL_VIEWS.includes(views.selectedGroupId) ? views.selectedGroupId.charAt(0).toUpperCase() + views.selectedGroupId.slice(1) : 'Default');
                                    })()}
                                </button>
                                <span className="focus-breadcrumb-sep">/</span>
                                <span className="focus-breadcrumb-current">{views.focusedChecklist.title || 'Untitled'}</span>
                            </div>
                            <div className="checklists-container">
                                <Checklist
                                    data={views.focusedChecklist}
                                    {...checklistCallbacks}
                                    isFocused
                                />
                            </div>
                        </>
                    ) : (
                        <>
                    {!views.isReadOnlyView && views.selectedGroupId !== 'all' && (
                        <div className="template-toolbar">
                            <button className="add-checklist-button" onClick={cl.openNewListModal}>
                                + New List
                            </button>
                            <div className="view-toggle">
                                <button
                                    className={`view-toggle-btn${views.viewMode === 'grid' ? ' active' : ''}`}
                                    onClick={() => views.toggleViewMode('grid')}
                                    title="Grid view"
                                >
                                    <GridIcon />
                                </button>
                                <button
                                    className={`view-toggle-btn${views.viewMode === 'columns' ? ' active' : ''}`}
                                    onClick={() => views.toggleViewMode('columns')}
                                    title="Column view"
                                >
                                    <ColumnsIcon />
                                </button>
                            </div>
                        </div>
                    )}
                    {cl.showModal ? (
                        <NewChecklistModal onConfirm={handleModalConfirm} onCancel={cl.closeModal} templates={cl.modalTemplates} groups={groups} defaultGroupId={views.selectedGroupId && !SPECIAL_VIEWS.includes(views.selectedGroupId) ? views.selectedGroupId : null}/>
                    ) : null}
                    {views.selectedGroupId === 'all' && (
                        <div className="timeline-toggle-header">
                            <div className="timeline-segmented-control">
                                <button className={`timeline-segment${!views.allItemsMode ? ' timeline-segment--active' : ''}`} onClick={() => views.setAllItemsMode(false)}>By Checklist</button>
                                <button className={`timeline-segment${views.allItemsMode ? ' timeline-segment--active' : ''}`} onClick={() => views.setAllItemsMode(true)}>By Item</button>
                            </div>
                        </div>
                    )}
                    {views.selectedGroupId === 'starred' && (
                        <div className="timeline-toggle-header">
                            <div className="timeline-segmented-control">
                                <button className={`timeline-segment${!views.starredItemsMode ? ' timeline-segment--active' : ''}`} onClick={() => views.setStarredItemsMode(false)}>By Checklist</button>
                                <button className={`timeline-segment${views.starredItemsMode ? ' timeline-segment--active' : ''}`} onClick={() => views.setStarredItemsMode(true)}>By Item</button>
                            </div>
                        </div>
                    )}
                    {views.selectedGroupId === 'scheduled' ? (
                        <ScheduledView
                            checklistData={cl.checklistData}
                            {...itemCallbacks}
                        />
                    ) : views.selectedGroupId === 'all' && views.allItemsMode ? (
                        <AllItemsView
                            groupedData={views.allGroupedItems}
                            {...itemCallbacks}
                        />
                    ) : views.selectedGroupId === 'starred' && views.starredItemsMode ? (
                        <AllItemsView
                            groupedData={views.starredGroupedItems}
                            {...itemCallbacks}
                        />
                    ) : (
                    <DndContext sensors={checklistSensors} collisionDetection={closestCenter} onDragEnd={handleChecklistDragEnd}>
                        <SortableContext items={views.visibleChecklists.map(c => c.id)} strategy={views.viewMode === 'grid' ? rectSortingStrategy : horizontalListSortingStrategy}>
                            <div className={`checklists-container${views.viewMode === 'grid' ? ' templates-grid' : ''}${views.focusedId !== null ? ' checklists-container--hidden' : ''}`}>
                                {views.visibleChecklists.length === 0 && (
                                    <div className="empty-state">
                                        <p className="empty-state-text">
                                            {views.selectedGroupId === 'starred'
                                                ? 'Zero stars. Your tasks have the charisma of a wet napkin.'
                                                : 'This group is emptier than your fridge on a Sunday night.'}
                                        </p>
                                    </div>
                                )}
                                {views.visibleChecklists.map(checklist => (
                                    <SortableChecklist
                                        key={checklist.id}
                                        checklist={checklist}
                                        isDragEnabled={views.isReorderable}
                                        {...checklistCallbacks}
                                        onStarItem={cl.starItem}
                                        onSetDueDateItem={cl.setDueDate}
                                        readOnly={views.isReadOnlyView}
                                        isFocused={views.focusedId === checklist.id}
                                    />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>
                    )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
